import { useState, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  Phone,
  Mail,
  CheckCircle,
  Copy,
} from 'lucide-react';
import { Button, Badge, Table, Modal } from '@/components/ui';
import { leadService } from '@/services/leadService';
import type { Lead, LeadStatus } from '@/types/lead';
import styles from './LeadsPage.module.css';

type BadgeVariant = 'neutral' | 'success' | 'warning' | 'info' | 'error';

const PAGE_SIZE = 20;

const ORIGIN_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Todas as origens' },
  { value: 'CNES', label: 'CNES' },
  { value: 'GOOGLE_PLACES', label: 'Google Places' },
  { value: 'CNPJA', label: 'CNPJA' },
  { value: 'APIFY_IMPORT', label: 'Apify Import' },
  { value: 'META_ADS', label: 'Meta Ads' },
  { value: 'MANUAL', label: 'Manual' },
  { value: 'WEBSITE', label: 'Website' },
];

const SCORE_CLASSIFICATION_COLORS: Record<string, { color: string; bgColor: string }> = {
  HOT: { color: '#721C24', bgColor: '#F8D7DA' },
  WARM: { color: '#856404', bgColor: '#FFF3CD' },
  COOL: { color: '#004085', bgColor: '#CCE5FF' },
  COLD: { color: '#495057', bgColor: '#E9ECEF' },
};

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
}

const STATUS_LABELS: Record<LeadStatus, string> = {
  NEW: 'Novo',
  CONTACTED: 'Contatado',
  CONVERTED: 'Convertido',
  LOST: 'Perdido',
};

const STATUS_BADGE: Record<LeadStatus, BadgeVariant> = {
  NEW: 'info',
  CONTACTED: 'warning',
  CONVERTED: 'success',
  LOST: 'error',
};

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function formatPhone(phone: string): string {
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return phone;
}

/**
 * Admin page for managing leads captured by campaigns.
 *
 * Features:
 * - Paginated table listing
 * - Filter by status and campaign
 * - Actions: mark contacted, convert (send invitation), mark lost
 */
export function LeadsPage() {
  const queryClient = useQueryClient();

  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<LeadStatus | ''>('');
  const [originFilter, setOriginFilter] = useState('');
  const [convertModalLead, setConvertModalLead] = useState<Lead | null>(null);
  const [invitationToken, setInvitationToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['leads', currentPage, statusFilter, originFilter],
    queryFn: () =>
      leadService.getLeads({
        page: currentPage,
        limit: PAGE_SIZE,
        status: statusFilter || undefined,
        origin: originFilter || undefined,
      }),
  });

  const leads = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 0;

  const contactMutation = useMutation({
    mutationFn: (id: string) => leadService.markContacted(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leads'] }),
  });

  const convertMutation = useMutation({
    mutationFn: (id: string) => leadService.convertLead(id),
    onSuccess: (response) => {
      setInvitationToken(response.invitationToken);
      queryClient.invalidateQueries({ queryKey: ['leads'] });
    },
  });

  const lostMutation = useMutation({
    mutationFn: (id: string) => leadService.markLost(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['leads'] }),
  });

  const handleStatusChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setStatusFilter(e.target.value as LeadStatus | '');
      setCurrentPage(1);
    },
    [],
  );

  const handleOriginChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setOriginFilter(e.target.value);
      setCurrentPage(1);
    },
    [],
  );

  const handleConvert = useCallback((lead: Lead) => {
    setConvertModalLead(lead);
    setInvitationToken(null);
    setCopied(false);
  }, []);

  const handleConfirmConvert = useCallback(() => {
    if (!convertModalLead) return;
    convertMutation.mutate(convertModalLead.id);
  }, [convertModalLead, convertMutation]);

  const handleCloseConvertModal = useCallback(() => {
    setConvertModalLead(null);
    setInvitationToken(null);
    setCopied(false);
  }, []);

  const handleCopyLink = useCallback(() => {
    if (!invitationToken) return;
    const link = `${window.location.origin}/portal/invite/${invitationToken}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [invitationToken]);

  const columns = [
    {
      key: 'lead',
      header: 'Lead',
      render: (lead: Lead) => (
        <div className={styles.leadInfo}>
          <span className={styles.leadName}>{lead.name}</span>
          {lead.campaignId && (
            <span className={styles.leadDetail}>Campanha vinculada</span>
          )}
        </div>
      ),
    },
    {
      key: 'contact',
      header: 'Contato',
      render: (lead: Lead) => (
        <div className={styles.contactInfo}>
          <span className={styles.contactDetail}>
            <Phone size={12} aria-hidden="true" /> {formatPhone(lead.phone)}
          </span>
          {lead.email ? (
            <span className={styles.contactDetail}>
              <Mail size={12} aria-hidden="true" /> {lead.email}
            </span>
          ) : (
            <span className={styles.contactEmpty}>Sem e-mail</span>
          )}
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (lead: Lead) => (
        <Badge variant={STATUS_BADGE[lead.status] ?? 'neutral'}>
          {STATUS_LABELS[lead.status] ?? lead.status}
        </Badge>
      ),
    },
    {
      key: 'score',
      header: 'Score',
      render: (lead: Lead) =>
        lead.scoreClassification ? (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0.125rem 0.5rem',
              borderRadius: '9999px',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 500,
              color: SCORE_CLASSIFICATION_COLORS[lead.scoreClassification]?.color ?? '#495057',
              backgroundColor: SCORE_CLASSIFICATION_COLORS[lead.scoreClassification]?.bgColor ?? '#E9ECEF',
            }}
          >
            {lead.score != null ? lead.score : ''} {lead.scoreClassification}
          </span>
        ) : (
          <span className={styles.dateText}>-</span>
        ),
    },
    {
      key: 'ltv',
      header: 'LTV',
      render: (lead: Lead) =>
        lead.ltvEstimated != null ? (
          <span>{formatCurrency(lead.ltvEstimated)}</span>
        ) : (
          <span className={styles.dateText}>-</span>
        ),
    },
    {
      key: 'date',
      header: 'Data',
      render: (lead: Lead) => (
        <span className={styles.dateText}>{formatDate(lead.createdAt)}</span>
      ),
    },
    {
      key: 'actions',
      header: 'Ações',
      render: (lead: Lead) => (
        <div className={styles.actions}>
          {lead.status === 'NEW' && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => contactMutation.mutate(lead.id)}
              isLoading={contactMutation.isPending}
            >
              Contatado
            </Button>
          )}
          {(lead.status === 'NEW' || lead.status === 'CONTACTED') && (
            <>
              <Button
                variant="primary"
                size="sm"
                onClick={() => handleConvert(lead)}
              >
                Converter
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => lostMutation.mutate(lead.id)}
                isLoading={lostMutation.isPending}
              >
                Perdido
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <h1 className={styles.title}>Leads</h1>
        <p className={styles.subtitle}>
          Gerencie leads capturados pelas campanhas.
        </p>
      </header>

      <div className={styles.filters}>
        <select
          className={styles.filterSelect}
          value={statusFilter}
          onChange={handleStatusChange}
          aria-label="Filtrar por status"
        >
          <option value="">Todos os status</option>
          <option value="NEW">Novo</option>
          <option value="CONTACTED">Contatado</option>
          <option value="CONVERTED">Convertido</option>
          <option value="LOST">Perdido</option>
        </select>
        <select
          className={styles.filterSelect}
          value={originFilter}
          onChange={handleOriginChange}
          aria-label="Filtrar por origem"
        >
          {ORIGIN_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <div className={styles.emptyState} role="status" aria-busy="true">
          <p>Carregando leads...</p>
        </div>
      ) : leads.length === 0 ? (
        <div className={styles.emptyState} role="status">
          <p>Nenhum lead encontrado.</p>
        </div>
      ) : (
        <>
          <Table columns={columns} data={leads} keyExtractor={(lead) => lead.id} />

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage <= 1}
                leftIcon={<ChevronLeft size={16} />}
              >
                Anterior
              </Button>
              <span className={styles.paginationInfo}>
                Página {currentPage} de {totalPages} ({totalCount} leads)
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage >= totalPages}
                leftIcon={<ChevronRight size={16} />}
              >
                Próxima
              </Button>
            </div>
          )}
        </>
      )}

      {/* Convert modal */}
      <Modal
        isOpen={convertModalLead !== null}
        onClose={handleCloseConvertModal}
        title="Converter lead"
        size="sm"
      >
        {invitationToken ? (
          <div className={styles.successMessage}>
            <CheckCircle size={32} className={styles.successIcon} aria-hidden="true" />
            <p>Convite criado com sucesso para <strong>{convertModalLead?.name}</strong>!</p>
            <div className={styles.invitationLink}>
              <span className={styles.invitationLinkText}>
                {window.location.origin}/portal/invite/{invitationToken}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={handleCopyLink}
                leftIcon={copied ? <CheckCircle size={14} /> : <Copy size={14} />}
              >
                {copied ? 'Copiado!' : 'Copiar'}
              </Button>
            </div>
            <Button variant="ghost" onClick={handleCloseConvertModal}>
              Fechar
            </Button>
          </div>
        ) : (
          <div>
            <p>
              Deseja criar um convite para <strong>{convertModalLead?.name}</strong>?
              O lead receberá um link para completar seu cadastro como paciente.
            </p>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem', justifyContent: 'flex-end' }}>
              <Button variant="ghost" onClick={handleCloseConvertModal}>
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmConvert}
                isLoading={convertMutation.isPending}
              >
                Criar convite
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
